/**
 * Traduz um tool_use do CLI para linguagem de negócio em PT-BR.
 *
 * Objetivo: a pessoa de produto/negócio entende "Lendo o arquivo X" muito
 * melhor do que "Read /workspace/src/foo.ts". Mantemos o que é informativo
 * (caminho curto, palavra-chave) mas escondemos jargão técnico.
 */
export function toolToBusinessStep(name: string, input: unknown): string {
  const obj = (input ?? {}) as Record<string, unknown>;

  // Helpers
  const shortPath = (p: string): string => {
    if (!p) return "";
    const clean = p.replace(/^\/workspace\/?/, "");
    const parts = clean.split("/").filter(Boolean);
    if (parts.length <= 3) return clean;
    return `${parts[0]}/…/${parts.slice(-2).join("/")}`;
  };

  const projectOf = (p: string): string | null => {
    if (!p) return null;
    const m = p.match(/^(?:\/workspace\/)?(projects|legados|docs|pessoal)\/([^/]+)/);
    return m && m[2] ? m[2] : null;
  };

  switch (name) {
    case "Read": {
      const file = String(obj.file_path ?? "");
      const proj = projectOf(file);
      const short = shortPath(file);
      if (proj) return `Lendo ${short} (projeto ${proj})`;
      if (short) return `Lendo arquivo ${short}`;
      return "Lendo um arquivo";
    }

    case "Glob": {
      const pattern = String(obj.pattern ?? "");
      const path = String(obj.path ?? "");
      const proj = projectOf(path);
      if (proj) return `Procurando arquivos "${pattern}" em ${proj}`;
      if (pattern) return `Procurando arquivos do padrão "${pattern}"`;
      return "Listando arquivos do workspace";
    }

    case "Grep": {
      const q = String(obj.pattern ?? "");
      const path = String(obj.path ?? "");
      const proj = projectOf(path);
      const term = q.length > 40 ? `${q.slice(0, 40)}…` : q;
      if (proj && term) return `Buscando "${term}" em ${proj}`;
      if (term) return `Buscando ocorrências de "${term}" no workspace`;
      return "Buscando ocorrências no código";
    }

    case "Bash": {
      const cmd = String(obj.command ?? "");
      const desc = String(obj.description ?? "");
      if (desc) return desc; // o CLI já manda a description em PT
      // heurística: comandos comuns
      if (/^git /.test(cmd)) return "Consultando o histórico do Git";
      if (/^ls\b/.test(cmd)) return "Listando arquivos de uma pasta";
      if (/^cat\b/.test(cmd)) return "Verificando o conteúdo de um arquivo";
      if (/^find\b/.test(cmd)) return "Procurando arquivos no sistema";
      if (/^grep\b/.test(cmd)) return "Buscando texto em arquivos";
      const trimmed = cmd.length > 50 ? `${cmd.slice(0, 50)}…` : cmd;
      return `Executando no terminal: ${trimmed}`;
    }

    case "WebFetch": {
      const url = String(obj.url ?? "");
      try {
        const host = new URL(url).hostname;
        return `Consultando ${host}`;
      } catch {
        return "Consultando uma página web";
      }
    }

    case "WebSearch": {
      const q = String(obj.query ?? "");
      return q ? `Pesquisando na web: "${q}"` : "Pesquisando na web";
    }

    case "TodoWrite":
      return "Organizando os próximos passos";

    case "Task":
      return "Acionando um especialista para investigar";

    default: {
      // Fallback genérico, ainda assim em PT
      const friendly = name
        .replace(/([A-Z])/g, " $1")
        .trim()
        .toLowerCase();
      return `Executando: ${friendly}`;
    }
  }
}
